import {
  Button,
  ColumnHeader,
  Icon,
  Select,
  TextField,
  type SelectOption
} from '@deque/cauldron-react';
import React, { useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAxeAccountAnalytics } from '~/common/contexts/analytics';
import { type AuthUser } from '~/common/contexts/auth';
import { type AllSettingsSchema } from '~/common/contexts/Configuration';
import { type Errors } from '~/common/utils/validate-general-settings-form';
import styles from './AccountSettings.css';

interface GeneralAccountSettingsProps {
  user?: AuthUser;
  settings: AllSettingsSchema | null;
  handleGeneralSubmit?: (e: React.ChangeEvent<HTMLFormElement>) => void;
  firstNameRef?: React.RefObject<HTMLInputElement | null>;
  lastNameRef?: React.RefObject<HTMLInputElement | null>;
  emailRef?: React.RefObject<HTMLInputElement | null>;
  jobFunctionRef?: React.RefObject<HTMLSelectElement | null>;
  languageRef?: React.RefObject<HTMLSelectElement | null>;
  submitting?: boolean;
  errors: Errors;
  shouldRestrictEmail?: boolean;
}

const GeneralAccountSettings = ({
  user,
  settings,
  handleGeneralSubmit,
  firstNameRef,
  lastNameRef,
  emailRef,
  jobFunctionRef,
  languageRef,
  submitting,
  errors,
  shouldRestrictEmail = false
}: GeneralAccountSettingsProps): ReactElement => {
  const { t } = useTranslation();
  const analytics = useAxeAccountAnalytics();

  useEffect(() => {
    analytics.viewAccountSettingsGeneralPage();
  }, []);

  const jobFunctionOptions: SelectOption[] = [
    ...(settings?.axeAccount.jobFunction.value
      ? []
      : [
          {
            key: '',
            value: '',
            label: t('Select a job function')
          }
        ]),
    {
      key: 'developer',
      value: 'developer',
      label: t('Developer')
    },
    {
      key: 'product manager/owner',
      value: 'product manager/owner',
      label: t('Product Manager/Owner')
    },
    {
      key: 'user experience designer',
      value: 'user experience designer',
      label: t('User Experience Designer')
    },
    {
      key: 'quality assurance tester',
      value: 'quality assurance tester',
      label: t('Quality Assurance Tester')
    },
    {
      key: 'business analyst',
      value: 'business analyst',
      label: t('Business Analyst')
    }
  ];

  const languageOptions: SelectOption[] = [
    {
      key: 'en',
      value: 'en',
      label: t('English')
    },
    {
      key: 'browser',
      value: 'browser',
      label: t('Browser default')
    },
    {
      key: 'es',
      value: 'es',
      label: t('Spanish')
    },
    {
      key: 'fr',
      value: 'fr',
      label: t('French')
    },
    {
      key: 'de',
      value: 'de',
      label: t('German')
    },
    {
      key: 'it',
      value: 'it',
      label: t('Italian')
    }
  ];

  return (
    <>
      <ColumnHeader>
        <h2>{t('General')}</h2>
      </ColumnHeader>
      <form onSubmit={handleGeneralSubmit}>
        <div className={styles.formWrap}>
          {shouldRestrictEmail ? (
            <div className={styles.readonlyEmail}>
              <span id="readonly-email-label" className="Field__label">
                {t('Email')}
              </span>
              <div
                aria-labelledby="readonly-email-label"
                className={styles.readonlyEmailValue}
              >
                {user?.email}
              </div>
              <p className={styles.readonlyEmailMessage}>
                <Icon type="lock" />
                {t(
                  'Managed by your organization. To update, contact your administrator.'
                )}
              </p>
            </div>
          ) : (
            <TextField
              label={t('Email')}
              name="email"
              type="email"
              defaultValue={user?.email}
              fieldRef={emailRef}
              error={errors.email}
              autoComplete="email"
              required
            />
          )}
          <TextField
            label={t('First name')}
            name="first-name"
            type="text"
            defaultValue={user?.firstName}
            fieldRef={firstNameRef}
            error={errors.firstName}
            autoComplete="given-name"
            required
          />
          <TextField
            label={t('Last name')}
            name="last-name"
            type="text"
            defaultValue={user?.lastName}
            fieldRef={lastNameRef}
            error={errors.lastName}
            autoComplete="family-name"
            required
          />
          <Select
            label={t('Job function')}
            name="job-function"
            ref={jobFunctionRef}
            options={jobFunctionOptions}
            defaultValue={settings?.axeAccount.jobFunction.value || ''}
          />
          <Select
            label={t('Language Preference')}
            name="language"
            ref={languageRef}
            options={languageOptions}
            defaultValue={settings?.axeAccount.language.value || ''}
          />
        </div>
        <div className={styles.formActions}>
          <Button type="submit" disabled={submitting}>
            {t('Save')}
          </Button>
        </div>
      </form>
    </>
  );
};

export default GeneralAccountSettings;
